import { debug as debugGen } from "debug";
import {
    UserActionReportReq,
    UserActionReport,
    ENUM_USER_ACTION_REPORT_TYPE
} from "../actionTypes";
import { Action, ActionType, ActionItem } from "../types";
import pageManager, { CachePage } from "../page";
import comInfo from "./comInfo";
import Compose from "./Compose";
import { userTag } from "./user";
import { push as pushCacheCompose } from "./cache";
import Expose, { ACTION_ID } from "./events/Expose";
import Click from "./events/Click";
import Setup from "./events/Setup";
import Pay from "./events/Pay";
import Stay from "./events/Stay";
import Custom from "./events/Custom";
import config from "../config";

const debug = debugGen("vtTracker:SDKCompose");

type MAP_ITEM = { [K: string]: typeof Compose };

const COMPOSE_MAP: MAP_ITEM = {
    click: Click,
    vtExpose: Expose,
    setupEvent: Setup,
    payStart: Pay,
    payResult: Pay,
    vtPageStay: Stay,
    vtCustomize: Custom
};

/**
 * 将上报信息封装成标准请求结构体
 * @param  {ActionItem}       action [description]
 * @return {UserActionReport}      [description]
 */
function action2Report(action: ActionItem): UserActionReport {
    const report: UserActionReport = {
        trace_info: null,
        page_info: null,
        to_uin: 0
    };

    let cp: CachePage = pageManager.getPage(action.appEl);

    if (typeof cp === "undefined") {
        cp = pageManager.addPage({
            appid: action.appid,
            pageid: action.page_id,
            node: action.appEl
        });
    }

    report.trace_info = cp.traceinfo;

    report.page_info = {
        appid: action.appid,
        page_id: action.page_id,
        item_infos: []
    };

    if (config.appidSuffix) {
        report.page_info.appid += config.appidSuffix;
    }

    report.page_info.item_infos.push({
        item_id: encodeURIComponent(action.item_id),
        sub_item_id: action.sub_item_id || [],
        module_id: action.module_id,
        sub_module_id: action.sub_module_id,
        position_id: action.position_id,
        rule_info: {
            test_id: action.test_id,
            rule_id: action.rule_id,
            str_ruleid: action.str_ruleid
        },
        action_infos: [
            {
                oper_time: Date.now(),
                action_id: action.action_id,
                action_value: action.action_value,
                action_attr: action.action_attr
            }
        ],
        trigger_info: action.trigger_info,
        busi_info: action.busi_info,
        item_type: action.item_type
    });

    if (action.to_uin) {
        report.to_uin = action.to_uin;
    }

    return report;
}

function getCompose(e: Event): Compose {
    if (COMPOSE_MAP[e.type]) {
        return new COMPOSE_MAP[e.type](e);
    }

    return null;
}

/**
 * 将事件行为封装成标准请求结构体
 * @param  {Array<Node>}         nodes [description]
 * @return {UserActionReportReq}       [description]
 */
function toReportReq(
    e: Event | Compose | ActionItem
): {
    el: Element;
    target: Element;
    immediate: boolean;
    req: UserActionReportReq;
} {
    const req: UserActionReportReq = {
        type: ENUM_USER_ACTION_REPORT_TYPE.RECOM_EFEECT_REPORT,
        user_comm_report: {
            ...comInfo,
            user_tag: userTag()
        },
        report_infos: []
    };

    let compose: Compose = null;
    let action: ActionItem = null;
    let target: Element = null;
    let immediate = false;

    if (e instanceof Compose || e instanceof Event) {
        compose = e instanceof Compose ? e : getCompose(e);

        if (compose === null) {
            return null;
        }

        debug("compose event target: %o", compose.target.tagName);

        action = compose.compose();

        target = compose.target;
    } else {
        action = e;

        if (action.appEl === null || typeof action.appEl === "undefined") {
            action.appEl = document.documentElement;
        }

        target = action.targetEl;
    }

    debug("compose result: %o", action);

    if (action === null) {
        return null;
    }

    // 非页面曝光的上报都必须要在页面曝光之后才能上报
    if (action.action_id !== ACTION_ID.PAGE) {
        if (
            compose !== null &&
            !(compose.page && compose.page.exposeComplete === true)
        ) {
            pushCacheCompose(compose);

            debug("push to cache for page not expose complete: %o", compose);

            return null;
        }

        // 直接通过report接口进行上报的没有compose
        const page = pageManager.getPage(action.appEl);

        if (!(page && page.exposeComplete === true)) {
            pushCacheCompose(action);

            debug("push to cache for page not expose complete: %d", action);

            return null;
        }
    }

    if (action.action_id === ACTION_ID.PAGE) {
        immediate = true;
    }

    if (typeof action.reportType !== "undefined") {
        req.type = action.reportType;
    }

    // 覆盖uin用户身份
    if (action.uin > 0) {
        req.user_comm_report.uin = action.uin;
    }

    req.report_infos.push(action2Report(action));

    return {
        el: action && action.appEl,
        immediate,
        target,
        req
    };
}

export default function(e: Event | Compose | ActionItem): Action {
    const req = toReportReq(e);

    return req === null
        ? null
        : {
              el: req.el,
              target: req.target,
              immediate: req.immediate,
              id: String(Date.now()),
              type: ActionType.SDK,
              content: JSON.stringify(req.req)
          };
}
